import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import {
  countCompliancePending,
  listComplianceBySync,
} from './complianceDb.js';
import {
  countExpensesPending,
  listExpensesBySync,
} from './expensesDb.js';
import { countReceiptsPending, listReceiptsBySync } from './receiptsDb.js';

export type SyncState = 'pending' | 'syncing' | 'synced' | 'failed';

export type DurationType = 'full_day' | 'half_day' | 'custom';

export type JobStatus = 'planned' | 'completed' | 'cancelled';

/** create/update flush via batch upsert; delete via DELETE /jobs/:id. */
export type JobPendingOp = 'create' | 'update' | 'delete' | null;

export type LocalJob = {
  id: string;
  work_date: string;
  client_id: string;
  work_type_id: string;
  duration_type: DurationType;
  custom_hours: number | null;
  custom_amount: number | null;
  site_reference: string | null;
  /** Ordered site ids for invoice narrative (M2M). */
  site_ids: string[];
  /** false = log time without producing an invoice line. */
  billable: boolean;
  /** planned = booking; completed = worked; cancelled = dropped. */
  status: JobStatus;
  start_time: string | null;
  confirmed_at: string | null;
  /** Week sign-off — set when ready for Saturday invoice run. */
  released_at: string | null;
  /** Xero draft InvoiceID while awaiting approval (not yet billed). */
  xero_invoice_id: string | null;
  po_number: string | null;
  notes: string | null;
  syncState: SyncState;
  failReason: string | null;
  invoice_run_id: string | null;
  /** Display label when invoiced (from invoice_runs). */
  invoice_number: string | null;
  /** Soft-delete marker — excluded from lists until hard-removed after sync. */
  deleted_at: string | null;
  pendingOp: JobPendingOp;
  createdAt: string;
  updatedAt: string;
  lastSyncAttemptAt: string | null;
  /** Set when this job was created from a receipt recharge. */
  recharge_receipt_id?: string | null;
};

export type XeroContactStatus =
  | 'mapped'
  | 'unmapped'
  | 'pending'
  | 'conflict'
  | 'failed';

export type XeroContactMatch = {
  ContactID: string;
  Name: string;
  EmailAddress?: string | null;
};

export type CachedClient = {
  id: string;
  name: string;
  day_rate: number | string | null;
  half_day_rate: number | string | null;
  hourly_rate: number | string | null;
  contact_name?: string | null;
  contact_email?: string | null;
  address?: string | null;
  payment_terms_days?: number | null;
  external_contact_id?: string | null;
  xero_contact_status?: XeroContactStatus;
  xero_contact_error?: string | null;
  xero_contact_matches?: XeroContactMatch[] | null;
  pendingOp?: 'create' | 'xero_push' | null;
  lastUsedAt: string | null;
};

export type CachedRateOverride = {
  id: string;
  client_id: string;
  work_type_id: string;
  day_rate: number | string;
  half_day_rate: number | string;
  hourly_rate: number | string | null;
};

export type CachedWorkType = {
  id: string;
  code: string;
  short_description: string;
  line_kind: 'day_rate' | 'fixed_fee' | 'expense';
  invoice_narrative?: string | null;
  half_day_suffix?: string | null;
};

export type CachedSite = {
  id: string;
  name: string;
  client_id: string | null;
  /** Server-derived frequency (job_log_sites + matching site_reference). */
  useCount: number;
  /** Local — updated when the site is selected on the entry form. */
  lastUsedAt: string | null;
};

interface JobsDB extends DBSchema {
  jobs: {
    key: string;
    value: LocalJob;
    indexes: {
      by_sync: SyncState;
      by_date: string;
      by_updated: string;
    };
  };
  clients: {
    key: string;
    value: CachedClient;
  };
  work_types: {
    key: string;
    value: CachedWorkType;
  };
  sites: {
    key: string;
    value: CachedSite;
  };
  rate_overrides: {
    key: string;
    value: CachedRateOverride;
  };
  meta: {
    key: string;
    value: { key: string; value: string };
  };
}

let dbPromise: Promise<IDBPDatabase<JobsDB>> | null = null;

/** Coerce PG/JSON dates (Date or ISO timestamp) to YYYY-MM-DD for calendar keys. */
export function dateOnly(
  v: string | Date | null | undefined,
): string | null {
  if (v == null) return null;
  if (v instanceof Date) {
    if (Number.isNaN(v.getTime())) return null;
    return v.toISOString().slice(0, 10);
  }
  const s = String(v).trim();
  if (!s) return null;
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1]! : null;
}

/** Normalise records written before billable/site_ids/status/pendingOp existed. */
export function normalizeLocalJob(raw: LocalJob): LocalJob {
  const workDate = dateOnly(raw.work_date) ?? raw.work_date;
  return {
    ...raw,
    work_date: workDate,
    site_ids: Array.isArray(raw.site_ids) ? raw.site_ids : [],
    billable: raw.billable !== false,
    status: raw.status ?? 'completed',
    start_time: raw.start_time ?? null,
    confirmed_at: raw.confirmed_at ?? null,
    released_at: raw.released_at ?? null,
    xero_invoice_id: raw.xero_invoice_id ?? null,
    invoice_number: raw.invoice_number ?? null,
    deleted_at: raw.deleted_at ?? null,
    pendingOp: raw.pendingOp ?? null,
    recharge_receipt_id: raw.recharge_receipt_id ?? null,
  };
}

export function getDb() {
  if (!dbPromise) {
    dbPromise = openDB<JobsDB>('terratech-jobs', 3, {
      upgrade(db, oldVersion) {
        if (oldVersion < 1) {
          const jobs = db.createObjectStore('jobs', { keyPath: 'id' });
          jobs.createIndex('by_sync', 'syncState');
          jobs.createIndex('by_date', 'work_date');
          jobs.createIndex('by_updated', 'updatedAt');
          db.createObjectStore('clients', { keyPath: 'id' });
          db.createObjectStore('work_types', { keyPath: 'id' });
          db.createObjectStore('meta', { keyPath: 'key' });
        }
        if (oldVersion < 2) {
          if (!db.objectStoreNames.contains('sites')) {
            db.createObjectStore('sites', { keyPath: 'id' });
          }
        }
        if (oldVersion < 3) {
          if (!db.objectStoreNames.contains('rate_overrides')) {
            db.createObjectStore('rate_overrides', { keyPath: 'id' });
          }
        }
      },
    });
  }
  return dbPromise;
}

/** Write-first path — must succeed offline. */
export async function putJobLocal(job: LocalJob): Promise<void> {
  const db = await getDb();
  await db.put('jobs', normalizeLocalJob(job));
}

export async function getJob(id: string): Promise<LocalJob | undefined> {
  const db = await getDb();
  const row = await db.get('jobs', id);
  return row ? normalizeLocalJob(row) : undefined;
}

export async function listJobsInRange(
  from: string,
  to: string,
): Promise<LocalJob[]> {
  const db = await getDb();
  const all = (await db.getAll('jobs')).map(normalizeLocalJob);
  return all
    .filter((j) => {
      if (j.deleted_at) return false;
      const day = dateOnly(j.work_date) ?? j.work_date;
      return day >= from && day <= to;
    })
    .sort((a, b) => {
      const byDate = a.work_date.localeCompare(b.work_date);
      if (byDate !== 0) return byDate;
      return a.createdAt.localeCompare(b.createdAt);
    });
}

export async function listJobsRecent(limit = 50): Promise<LocalJob[]> {
  const db = await getDb();
  const all = (await db.getAll('jobs'))
    .map(normalizeLocalJob)
    .filter((j) => !j.deleted_at);
  all.sort((a, b) => {
    const byDate = b.work_date.localeCompare(a.work_date);
    if (byDate !== 0) return byDate;
    return b.createdAt.localeCompare(a.createdAt);
  });
  return all.slice(0, limit);
}

export async function listPendingConfirmationsLocal(
  todayIso: string,
): Promise<LocalJob[]> {
  const db = await getDb();
  const all = (await db.getAll('jobs')).map(normalizeLocalJob);
  return all
    .filter((j) => {
      if (j.deleted_at || j.status !== 'planned') return false;
      const day = dateOnly(j.work_date) ?? j.work_date;
      return day < todayIso;
    })
    .sort((a, b) => a.work_date.localeCompare(b.work_date));
}

export type RemoteJobMerge = {
  id: string;
  work_date: string;
  client_id: string;
  work_type_id: string;
  duration_type: DurationType;
  custom_hours: number | string | null;
  custom_amount: number | string | null;
  site_reference: string | null;
  site_ids?: string[];
  po_number: string | null;
  notes: string | null;
  billable: boolean;
  status: JobStatus;
  start_time: string | null;
  confirmed_at: string | null;
  released_at?: string | null;
  xero_invoice_id?: string | null;
  invoice_run_id: string | null;
  invoice_number?: string | null;
  created_at: string;
  updated_at: string;
  recharge_receipt_id?: string | null;
};

/** Pull server jobs into IndexedDB. Skips locals with unsynced pending ops. */
export async function mergeJobsFromServer(
  remote: RemoteJobMerge[],
): Promise<void> {
  for (const r of remote) {
    const local = await getJob(r.id);
    const invoiceNumber = r.invoice_number ?? null;
    if (
      local &&
      local.syncState !== 'synced' &&
      (local.pendingOp ||
        local.syncState === 'pending' ||
        local.syncState === 'syncing' ||
        local.syncState === 'failed')
    ) {
      // Still absorb invoice lock so the UI freezes correctly.
      if (r.invoice_run_id && !local.invoice_run_id) {
        await putJobLocal({
          ...local,
          invoice_run_id: r.invoice_run_id,
          xero_invoice_id: r.xero_invoice_id ?? local.xero_invoice_id,
          released_at: r.released_at ?? local.released_at,
          invoice_number: invoiceNumber ?? local.invoice_number,
        });
      } else if (
        invoiceNumber &&
        local.invoice_run_id &&
        !local.invoice_number
      ) {
        await putJobLocal({ ...local, invoice_number: invoiceNumber });
      } else if (r.released_at && !local.released_at) {
        await putJobLocal({
          ...local,
          released_at: r.released_at,
          xero_invoice_id: r.xero_invoice_id ?? local.xero_invoice_id,
        });
      }
      continue;
    }

    const customHours =
      r.custom_hours == null ? null : Number(r.custom_hours);
    const customAmount =
      r.custom_amount == null ? null : Number(r.custom_amount);

    await putJobLocal({
      id: r.id,
      work_date: dateOnly(r.work_date) ?? String(r.work_date).slice(0, 10),
      client_id: r.client_id,
      work_type_id: r.work_type_id,
      duration_type: r.duration_type,
      custom_hours: customHours,
      custom_amount: customAmount,
      site_reference: r.site_reference,
      site_ids: Array.isArray(r.site_ids) ? r.site_ids : local?.site_ids ?? [],
      billable: r.billable !== false,
      status: r.status ?? 'completed',
      start_time: r.start_time,
      confirmed_at: r.confirmed_at,
      released_at: r.released_at ?? null,
      xero_invoice_id: r.xero_invoice_id ?? null,
      po_number: r.po_number,
      notes: r.notes,
      syncState: 'synced',
      failReason: null,
      invoice_run_id: r.invoice_run_id,
      invoice_number: invoiceNumber,
      deleted_at: null,
      pendingOp: null,
      createdAt: local?.createdAt ?? r.created_at,
      updatedAt: r.updated_at,
      lastSyncAttemptAt: local?.lastSyncAttemptAt ?? null,
      recharge_receipt_id: r.recharge_receipt_id ?? local?.recharge_receipt_id ?? null,
    });
  }
}

export async function listJobsBySync(state: SyncState): Promise<LocalJob[]> {
  const db = await getDb();
  return (await db.getAllFromIndex('jobs', 'by_sync', state)).map(
    normalizeLocalJob,
  );
}

export async function countPendingAndFailed(): Promise<{
  pending: number;
  failed: number;
}> {
  const pending = (await listJobsBySync('pending')).length;
  const syncing = (await listJobsBySync('syncing')).length;
  const failed = (await listJobsBySync('failed')).length;
  const cPending = await countCompliancePending();
  const cFailed = (await listComplianceBySync('failed')).length;
  const ePending = await countExpensesPending();
  const eFailed = (await listExpensesBySync('failed')).length;
  const rPending = await countReceiptsPending();
  const rFailed = (await listReceiptsBySync('failed')).length;
  const clients = await listClientsCached();
  const clientPending = clients.filter(
    (c) => c.pendingOp === 'create' || c.pendingOp === 'xero_push',
  ).length;
  const clientFailed = clients.filter(
    (c) =>
      !c.pendingOp &&
      (c.xero_contact_status === 'failed' || c.xero_contact_status === 'conflict'),
  ).length;
  return {
    pending: pending + syncing + cPending + ePending + rPending + clientPending,
    failed: failed + cFailed + eFailed + rFailed + clientFailed,
  };
}

export async function deleteJobLocal(id: string): Promise<void> {
  const db = await getDb();
  await db.delete('jobs', id);
}

export async function replaceClients(clients: CachedClient[]): Promise<void> {
  const db = await getDb();
  const existing = await db.getAll('clients');
  const lastUsed = new Map(existing.map((c) => [c.id, c.lastUsedAt] as const));
  const pending = existing.filter(
    (c) => c.pendingOp === 'create' || c.pendingOp === 'xero_push',
  );
  const tx = db.transaction('clients', 'readwrite');
  await tx.store.clear();
  const seen = new Set<string>();
  for (const c of clients) {
    seen.add(c.id);
    await tx.store.put({
      ...c,
      lastUsedAt: lastUsed.get(c.id) ?? c.lastUsedAt ?? null,
      pendingOp: null,
    });
  }
  for (const local of pending) {
    if (!seen.has(local.id)) await tx.store.put(local);
  }
  await tx.done;
}

export async function putClientCached(client: CachedClient): Promise<void> {
  const db = await getDb();
  await db.put('clients', client);
}

export async function replaceWorkTypes(
  types: CachedWorkType[],
): Promise<void> {
  const db = await getDb();
  const tx = db.transaction('work_types', 'readwrite');
  await tx.store.clear();
  for (const t of types) await tx.store.put(t);
  await tx.done;
}

export async function replaceSites(sites: CachedSite[]): Promise<void> {
  const db = await getDb();
  const existing = await db.getAll('sites');
  const lastUsed = new Map(
    existing.map((s) => [s.id, s.lastUsedAt ?? null] as const),
  );
  const tx = db.transaction('sites', 'readwrite');
  await tx.store.clear();
  for (const s of sites) {
    await tx.store.put({
      ...s,
      useCount: s.useCount ?? 0,
      lastUsedAt: lastUsed.get(s.id) ?? s.lastUsedAt ?? null,
    });
  }
  await tx.done;
}

export async function putSiteCached(site: CachedSite): Promise<void> {
  const db = await getDb();
  await db.put('sites', {
    ...site,
    useCount: site.useCount ?? 0,
    lastUsedAt: site.lastUsedAt ?? null,
  });
}

export async function touchSiteUsed(siteId: string): Promise<void> {
  const db = await getDb();
  const s = await db.get('sites', siteId);
  if (!s) return;
  s.lastUsedAt = new Date().toISOString();
  s.useCount = (s.useCount ?? 0) + 1;
  await db.put('sites', s);
}

export async function replaceRateOverrides(
  rows: CachedRateOverride[],
): Promise<void> {
  const db = await getDb();
  const tx = db.transaction('rate_overrides', 'readwrite');
  await tx.store.clear();
  for (const r of rows) await tx.store.put(r);
  await tx.done;
}

export async function listRateOverridesCached(): Promise<CachedRateOverride[]> {
  const db = await getDb();
  return db.getAll('rate_overrides');
}

export async function listClientsCached(): Promise<CachedClient[]> {
  const db = await getDb();
  const all = await db.getAll('clients');
  return all
    .map((c) => ({
      ...c,
      day_rate: c.day_rate ?? null,
      half_day_rate: c.half_day_rate ?? null,
      hourly_rate: c.hourly_rate ?? null,
    }))
    .sort((a, b) => {
    if (a.lastUsedAt && b.lastUsedAt) {
      return b.lastUsedAt.localeCompare(a.lastUsedAt);
    }
    if (a.lastUsedAt) return -1;
    if (b.lastUsedAt) return 1;
    return a.name.localeCompare(b.name);
  });
}

export async function listWorkTypesCached(): Promise<CachedWorkType[]> {
  const db = await getDb();
  const all = await db.getAll('work_types');
  return all.sort((a, b) => a.code.localeCompare(b.code));
}

export async function listSitesCached(clientId?: string): Promise<CachedSite[]> {
  const db = await getDb();
  const all = await db.getAll('sites');
  const filtered = clientId
    ? all.filter((s) => s.client_id === null || s.client_id === clientId)
    : all;
  return filtered
    .map((s) => ({
      ...s,
      useCount: s.useCount ?? 0,
      lastUsedAt: s.lastUsedAt ?? null,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export async function touchClientUsed(clientId: string): Promise<void> {
  const db = await getDb();
  const c = await db.get('clients', clientId);
  if (!c) return;
  c.lastUsedAt = new Date().toISOString();
  await db.put('clients', c);
}

export async function getMeta(key: string): Promise<string | undefined> {
  const db = await getDb();
  return (await db.get('meta', key))?.value;
}

export async function setMeta(key: string, value: string): Promise<void> {
  const db = await getDb();
  await db.put('meta', { key, value });
}

/** For tests */
export async function __resetDbForTests(): Promise<void> {
  if (dbPromise) {
    const db = await dbPromise;
    db.close();
    dbPromise = null;
  }
  await new Promise<void>((resolve, reject) => {
    const req = indexedDB.deleteDatabase('terratech-jobs');
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}
